import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ContactGroupService } from 'src/app/private/services/contact-group.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import * as Papa from 'papaparse';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { FileUploadModule } from 'primeng/fileupload';

@Component({
  selector: 'app-bulk-import-contact',
  imports: [SharedModule, FileUploadModule],
  templateUrl: './bulk-import-contact.component.html',
  styleUrl: './bulk-import-contact.component.scss'
})
export class BulkImportContactComponent {
  @ViewChild('fileUpload') fileUpload: any;
  
  csvFile: File | null = null;
  csvUploaded = false;
  columns: any[] = []; // Updated to match PrimeNG dropdown format
  csvData: any[] = [];
  mappedData: any[] = [];
  form: FormGroup;
  btnLoading = false;

  @Output() reload: EventEmitter<any> = new EventEmitter();
  @Input() selectedGroupId: any;

  @Output() closeDialog: EventEmitter<any> = new EventEmitter();

  constructor(private fb: FormBuilder,
    private api: ContactGroupService,
    private localStorage: LocalStorageService,
    private messageService: MessageService
  ) {
    // Create form group with required validators
    this.form = this.fb.group({
      first_name: ['', Validators.required],
      last_name: [''],
      email: ['', Validators.required],
      phone: ['', Validators.required]
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        this.csvFile = file;
        // Create a dummy event object that matches p-fileUpload's expected format
        this.onFileSelect({ files: [file] });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please upload a CSV file'
        });
      }
    }
  }

  // Method called when a file is selected
  onFileSelect(event: any) {
    const file = event.files[0]; // Changed to match PrimeNG fileUpload event structure

    if (file) {
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        this.csvFile = file;
        this.parseCSV(file);
        this.csvUploaded = true;
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please upload a CSV file'
        });
      }
    }
  }

  onFileClear() {
    this.csvFile = null;
    this.csvUploaded = false;
    this.csvData = [];
    this.columns = [];
    this.form.reset();
  }

  onFileRemove(event: any) {
    this.csvFile = null;
    this.csvUploaded = false;
    this.csvData = [];
    this.columns = [];
    this.form.reset();
  }

  goBackToUpload() {
    this.csvFile = null;
    this.csvUploaded = false;
    this.csvData = [];
    this.columns = [];
    this.form.reset();
  }

  downloadSampleCSV() {
    // Sample CSV data with headers for contact group
    const csvContent = `first_name,last_name,email,phone
John,Doe,john.doe@example.com,+1234567890
Jane,Smith,jane.smith@example.com,+0987654321
Mike,Johnson,mike.johnson@example.com,+1122334455`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'sample-contact-group.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Download Started',
      detail: 'Sample CSV file has been downloaded'
    });
  }

  // Parse the CSV file
  parseCSV(file: File) {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        this.csvData = result.data;
        // Extract columns in PrimeNG dropdown format
        if (this.csvData.length > 0) {
          this.columns = Object.keys(this.csvData[0]).map(col => ({ label: col, value: col }));
        }
      }
    });
  }

  handleCloseDialog() {
    this.closeDialog.emit();
  }

  // Method to map data based on user selection
  mapData() {
    if (this.form.invalid) {
      // Mark all fields as touched to show validation errors
      this.form.markAllAsTouched();
      return;
    }

    const selectedFirstName = this.form.get('first_name')?.value;
    const selectedLastName = this.form.get('last_name')?.value;
    const selectedEmail = this.form.get('email')?.value;
    const selectedPhone = this.form.get('phone')?.value;

    this.mappedData = this.csvData.map((row) => ({
      first_name: row[selectedFirstName],
      last_name: row[selectedLastName],
      source: 'csv',
      email: row[selectedEmail],
      phone: row[selectedPhone]
    }));

    const payload = {
      organization_id: this.localStorage.getOrganizationId(),
      contact_group_id: this.selectedGroupId,
      contacts: this.mappedData,
      user_id: this.localStorage.getUserId()
    };
    this.btnLoading = true;
    this.api.createContactGroupList(payload).subscribe(
      (response: any) => {
        this.btnLoading = false;
        this.reload.emit();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Contacts imported successfully' });
        this.handleCloseDialog();
      },
      (error: any) => {
        console.log(error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add contacts' });
        this.btnLoading = false;
      }
    );
  }

  // Helper method to check if a form control is invalid and touched
  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field!.invalid && field!.touched;
  }
}